import { round } from "lodash";
import moment from "moment";

export const getWeeklyPeriodsUntilEndDate = (
  startDate,
  endDate,
  startingCalendarDay
) => {
  let weeklyPeriods = {};

  let { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    startDate,
    startingCalendarDay
  );

  while (endOfWeek.isSameOrBefore(moment(endDate).add(7, "days"))) {
    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${endOfWeek.format(
      "YYYY-MM-DD"
    )}`;

    weeklyPeriods[period] = {
      startOfWeek: startOfWeek.toISOString(),
      endOfWeek: endOfWeek.toISOString(),
      isPeriod: true,
      isSingle: false,
      nutrients: {
        proteins: 0,
        carbs: 0,
        fats: 0,
        fibers: 0,
      },
      nutrientsDays: 0,
      proteinsDays: 0,
      carbsDays: 0,
      fatsDays: 0,
      fibersDays: 0,
      steps: 0,
      stepsDays: 0,
      bodyWeight: 0,
      bodyWeightDays: 0,
      hasWorkout: false,
      workoutDays: 0,
      utcDate: Number(startOfWeek.format("YYYYMMDD")),
    };

    startOfWeek.add(7, "days");
    endOfWeek.add(7, "days");
  }

  return weeklyPeriods;
};

export const getWeekPeriodFromDate = (startDate, startingCalendarDay) => {
  const startOfPeriod = moment(startDate);

  const isoWeekday =
    startOfPeriod.isoWeekday() === 7 ? 0 : startOfPeriod.isoWeekday();
  const daysToSubtract =
    isoWeekday >= startingCalendarDay
      ? isoWeekday - startingCalendarDay
      : 7 + isoWeekday - startingCalendarDay;

  const startOfWeek = startOfPeriod.clone().subtract(daysToSubtract, "days");

  const endOfWeek = startOfWeek.clone().add(6, "days");

  return { startOfWeek, endOfWeek };
};

export const addStatsToPeriod = (weeklyPeriod, stat) => {
  if (stat) {
    if (stat.nutrients) {
      weeklyPeriod.nutrients.proteins += stat.nutrients.proteins || 0;
      weeklyPeriod.nutrients.carbs += stat.nutrients.carbs || 0;
      weeklyPeriod.nutrients.fats += stat.nutrients.fats || 0;
      weeklyPeriod.nutrients.fibers += stat.nutrients.fibers || 0;

      // Gets the accurate number of days for each nutrient
      // nutrientsDays is used for the total kCal in other functions
      const hasAnyValue = Object.values(stat.nutrients).some(
        (v) => (v ?? 0) > 0
      );
      if (hasAnyValue) {
        weeklyPeriod.nutrientsDays += 1;
      }
      weeklyPeriod.proteinsDays += stat.nutrients.proteins ? 1 : 0;
      weeklyPeriod.carbsDays += stat.nutrients.carbs ? 1 : 0;
      weeklyPeriod.fatsDays += stat.nutrients.fats ? 1 : 0;
      weeklyPeriod.fibersDays += stat.nutrients.fibers ? 1 : 0;
    }

    if (stat.steps) {
      weeklyPeriod.steps += stat.steps || 0;
      weeklyPeriod.stepsDays += 1;
    }

    if (stat.bodyWeight) {
      weeklyPeriod.bodyWeight += stat.bodyWeight || 0;
      weeklyPeriod.bodyWeightDays += 1;
    }

    if (stat.foodLog || stat.internalFoodLog) {
      if (!weeklyPeriod.foodLog) {
        weeklyPeriod.foodLog = [];
      }

      if (stat.foodLog) {
        weeklyPeriod.foodLog.push({
          foodLog: stat.foodLog,
          date: stat.utcDate,
        });
      }

      if (stat.internalFoodLog) {
        weeklyPeriod.foodLog.push({
          foodLog: stat.internalFoodLog,
          date: stat.utcDate,
        });
      }
    }
  }
};

export const getFilteredStats = (
  dateFrom,
  dateTo,
  dailyStats,
  activeExercises
) => {
  const statsInDateRange = dailyStats.filter(
    (stat) =>
      stat.utcDate >= Number(moment(dateFrom).format("YYYYMMDD")) &&
      stat.utcDate <= Number(moment(dateTo).format("YYYYMMDD"))
  );

  const statsWithoutWeekAverage = statsInDateRange.map((stat) => {
    const exercise = activeExercises.find((ex) => ex.utcDate === stat.utcDate);
    stat.hasWorkout = !!exercise;
    return stat;
  });

  const result = statsWithoutWeekAverage.sort((a, b) => b.utcDate - a.utcDate);

  return result;
};

export const getAverageStats = (
  dateFrom,
  dateTo,
  startingCalendarDay,
  dailyStats
) => {
  const statsInDateRange = dailyStats.filter(
    (stat) =>
      stat.utcDate >= Number(moment(dateFrom).format("YYYYMMDD")) &&
      stat.utcDate <= Number(moment(dateTo).format("YYYYMMDD"))
  );

  let weeklyPeriods = Object.assign(
    {},
    getWeeklyPeriodsUntilEndDate(dateFrom, dateTo, startingCalendarDay)
  );

  statsInDateRange.forEach((stat) => {
    const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
      String(stat.utcDate),
      startingCalendarDay
    );

    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${endOfWeek.format(
      "YYYY-MM-DD"
    )}`;

    addStatsToPeriod(weeklyPeriods[period], stat);
  });

  const result = Object.values(weeklyPeriods).sort(
    (a, b) => b.utcDate - a.utcDate
  );

  return result;
};

export const getSingleAverageStat = (dateFrom, dateTo, dailyStats) => {
  const statsInDateRange = dailyStats.filter(
    (stat) =>
      stat.utcDate >= Number(moment(dateFrom).format("YYYYMMDD")) &&
      stat.utcDate <= Number(moment(dateTo).format("YYYYMMDD"))
  );

  const startOfWeek = moment(dateFrom);
  const endOfWeek = moment(dateTo);

  let weeklyPeriods = {};

  const period = `${startOfWeek.format("YYYY-MM-DD")} - ${endOfWeek.format(
    "YYYY-MM-DD"
  )}`;

  weeklyPeriods[period] = {
    startOfWeek: startOfWeek.toISOString(),
    endOfWeek: endOfWeek.toISOString(),
    isPeriod: true,
    isSingle: true,
    nutrients: {
      proteins: 0,
      carbs: 0,
      fats: 0,
      fibers: 0,
    },
    nutrientsDays: 0,
    proteinsDays: 0,
    carbsDays: 0,
    fatsDays: 0,
    fibersDays: 0,
    steps: 0,
    stepsDays: 0,
    bodyWeight: 0,
    bodyWeightDays: 0,
    hasWorkout: false,
    workoutDays: 0,
    utcDate: Number(endOfWeek.format("YYYYMMDD")) + 1,
  };

  statsInDateRange.forEach((stat) => {
    addStatsToPeriod(weeklyPeriods[period], stat);
  });

  const result = Object.values(weeklyPeriods).sort(
    (a, b) => b.utcDate - a.utcDate
  );

  return result;
};

export const getActiveDayStats = (
  activeDayUtc,
  stats,
  averageStats,
  isPeriod = false
) => {
  if (isPeriod) {
    return averageStats.find(
      (averageStat) => averageStat.utcDate === activeDayUtc
    );
  }

  return stats.find((stat) => stat.utcDate === activeDayUtc);
};

export const getNutritionData = (
  nutrients,
  units,
  steps,
  isPeriod,
  bodyWeight,
  bodyWeightDays,
  nutrientsDays,
  stepsDays,
  sleep,
  proteinsDays = 1,
  carbsDays = 1,
  fatsDays = 1,
  fibersDays = 1
) => {
  const { proteins, carbs, fats, fibers } = nutrients || 0;
  const proteinsKcal = proteins ? proteins * 4 : 0;
  const carbsKcal = carbs ? carbs * 4 : 0;
  const fatsKcal = fats ? fats * 9 : 0;
  const kcal = proteinsKcal + carbsKcal + fatsKcal;

  const convertedKCal = isPeriod ? kcal / nutrientsDays : kcal;

  const convertedProteins = isPeriod ? proteins / proteinsDays : proteins;
  const convertedCarbs = isPeriod ? carbs / carbsDays : carbs;
  const convertedFats = isPeriod ? fats / fatsDays : fats;
  const convertedFibers = isPeriod ? fibers / fibersDays : fibers;

  const bodyWeightAverage = isPeriod ? bodyWeight / bodyWeightDays : bodyWeight;

  const convertedBodyWeight =
    units === "metric" ? bodyWeightAverage : bodyWeightAverage * 2.205;

  const convertedSteps = isPeriod ? steps / stepsDays : steps;

  return {
    proteins: convertedProteins,
    carbs: convertedCarbs,
    fats: convertedFats,
    fibers: convertedFibers,
    kcal: convertedKCal,
    convertedBodyWeight,
    steps: convertedSteps,
    isPeriod: isPeriod || false,
    sleep,
  };
};

// P×4 + C×4 + F×9 — same formula as utils/nutrition-progress/adherence.js's
// kcalFromMacros, kept as a plain local helper here rather than importing
// the newer engine into this legacy util module.
const derivedKcal = (proteins = 0, carbs = 0, fats = 0) =>
  Math.round((proteins || 0) * 4 + (carbs || 0) * 4 + (fats || 0) * 9);

export const getTargetData = ({
  nutritionPlan,
  isPeriod,
  startOfWeek,
  endOfWeek,
  averageValues = false,
  hasWorkout = false,
  activeExercises,
  isPlanCalendar = false,
}) => {
  if (isPeriod) {
    let proteins = 0;
    let fats = 0;
    let carbs = 0;
    let fibers = 0;
    let kCals = 0;

    // Start at 0 to not cause issues
    let proteinDays = 0;
    let carbsDays = 0;
    let fatsDays = 0;
    let fibersDays = 0;
    let kCalDays = 0;

    nutritionPlan.forEach((plan) => {
      const nutritionStart = moment.utc(plan.startDate, "YYYYMMDD");
      const nutritionEnd = moment.utc(plan.endDate, "YYYYMMDD");
      const periodStart = moment.utc(startOfWeek, "YYYYMMDD");
      const periodEnd = moment.utc(endOfWeek, "YYYYMMDD");

      const startDate = moment.max(nutritionStart, periodStart);
      const endDate = moment.min(nutritionEnd, periodEnd);

      // Number of days to get the total of the plan for the period of time
      const numberOfDays = endDate.diff(startDate, "days") + 1;

      if (!plan.restDay || isPlanCalendar) {
        // If there is data, add that data to the variables and multiply for the number of days
        proteins += plan.proteins ? plan.proteins * numberOfDays : 0;
        fats += plan.fats ? plan.fats * numberOfDays : 0;
        carbs += plan.carbs ? plan.carbs * numberOfDays : 0;
        fibers += plan.fibers ? plan.fibers * numberOfDays : 0;

        // To accurately get the number of days for the average
        proteinDays += plan.proteins ? numberOfDays : 0;
        carbsDays += plan.carbs ? numberOfDays : 0;
        fatsDays += plan.fats ? numberOfDays : 0;
        fibersDays += plan.fibers ? numberOfDays : 0;

        const proteinsKcal = plan.proteins ? round(plan.proteins * 4, 0) : 0;
        const carbsKcal = plan.carbs ? round(plan.carbs * 4, 0) : 0;
        const fatsKcal = plan.fats ? round(plan.fats * 9, 0) : 0;
        const totalKCals = proteinsKcal + carbsKcal + fatsKcal;

        kCalDays += totalKCals ? numberOfDays : 0;

        kCals += totalKCals ? totalKCals * numberOfDays : 0;
      } else {
        const workoutDays = getWorkoutDaysOnRange(
          startDate,
          endDate,
          activeExercises
        );

        const daysWithoutWorkouts = numberOfDays - workoutDays;

        // If there is data, add that data to the variables and multiply for the number of days
        proteins += plan.proteins ? plan.proteins * workoutDays : 0;
        fats += plan.fats ? plan.fats * workoutDays : 0;
        carbs += plan.carbs ? plan.carbs * workoutDays : 0;
        fibers += plan.fibers ? plan.fibers * workoutDays : 0;

        // To accurately get the number of days for the average
        proteinDays += plan.proteins ? workoutDays : 0;
        carbsDays += plan.carbs ? workoutDays : 0;
        fatsDays += plan.fats ? workoutDays : 0;
        fibersDays += plan.fibers ? workoutDays : 0;

        // If there is data, add that data to the variables and multiply for the number of days
        proteins += plan?.restDay?.proteins
          ? plan?.restDay?.proteins * daysWithoutWorkouts
          : 0;
        fats += plan?.restDay?.fats
          ? plan?.restDay?.fats * daysWithoutWorkouts
          : 0;
        carbs += plan?.restDay?.carbs
          ? plan?.restDay?.carbs * daysWithoutWorkouts
          : 0;
        fibers += plan?.restDay?.fibers
          ? plan?.restDay?.fibers * daysWithoutWorkouts
          : 0;

        // To accurately get the number of days for the average
        proteinDays += plan?.restDay?.proteins ? daysWithoutWorkouts : 0;
        carbsDays += plan?.restDay?.carbs ? daysWithoutWorkouts : 0;
        fatsDays += plan?.restDay?.fats ? daysWithoutWorkouts : 0;
        fibersDays += plan?.restDay?.fibers ? daysWithoutWorkouts : 0;

        // kCals
        const proteinsKcal = plan.proteins ? round(plan.proteins * 4, 0) : 0;
        const carbsKcal = plan.carbs ? round(plan.carbs * 4, 0) : 0;
        const fatsKcal = plan.fats ? round(plan.fats * 9, 0) : 0;
        const trainigDayKcals = proteinsKcal + carbsKcal + fatsKcal;

        const restDayProteinsKcal = plan?.restDay?.proteins
          ? round(plan?.restDay?.proteins * 4, 0)
          : 0;
        const restDayCarbsKcal = plan?.restDay?.carbs
          ? round(plan?.restDay?.carbs * 4, 0)
          : 0;
        const restDayFatsKcal = plan?.restDay?.fats
          ? round(plan?.restDay?.fats * 9, 0)
          : 0;
        const restDayKcals =
          restDayProteinsKcal + restDayCarbsKcal + restDayFatsKcal;

        const totalKCals =
          (trainigDayKcals * workoutDays + restDayKcals * daysWithoutWorkouts) /
          numberOfDays;

        kCalDays += totalKCals ? numberOfDays : 0;

        kCals += totalKCals ? totalKCals * numberOfDays : 0;
      }
    });

    // Condition in case the values are 0
    if (averageValues) {
      proteins = proteins / (proteinDays ? proteinDays : 1);
      fats = fats / (fatsDays ? fatsDays : 1);
      carbs = carbs / (carbsDays ? carbsDays : 1);
      fibers = fibers / (fibersDays ? fibersDays : 1);
      kCals = kCals / (kCalDays ? kCalDays : 1);
    }

    return {
      targetProteins: proteins,
      targetFats: fats,
      targetCalories: kCals,
      targetCarbs: carbs,
      targetFibers: fibers,
    };
  } else {
    if (nutritionPlan?.restDay && !hasWorkout) {
      const { restDay } = nutritionPlan || null;

      const {
        proteins: targetProteins,
        fats: targetFats,
        calories: targetCalories,
        carbs: targetCarbs,
        fibers: targetFibers,
      } = restDay || 0;

      return {
        targetProteins,
        targetFats,
        // Falls back to derived P×4+C×4+F×9 when the plan's rest-day target
        // has no stored `calories` — mirrors utils/nutrition-progress/dayData.js's
        // targetForDay, so every caller of this function (Table legacy, the
        // athlete's own calendar Log, Nutrition Progress) computes the same
        // target kcal instead of only some of them deriving it.
        targetCalories:
          targetCalories ||
          derivedKcal(targetProteins, targetCarbs, targetFats),
        targetCarbs,
        targetFibers,
      };
    } else {
      const {
        proteins: targetProteins,
        fats: targetFats,
        calories: targetCalories,
        carbs: targetCarbs,
        fibers: targetFibers,
      } = nutritionPlan || 0;

      return {
        targetProteins,
        targetFats,
        targetCalories:
          targetCalories ||
          derivedKcal(targetProteins, targetCarbs, targetFats),
        targetCarbs,
        targetFibers,
      };
    }
  }
};

export const getRestDayTargetData = (
  nutritionPlan,
  startOfWeek,
  endOfWeek,
  averageValues = false
) => {
  let proteins = 0;
  let fats = 0;
  let carbs = 0;
  let fibers = 0;
  let kCals = 0;

  // Start at 0 to not cause issues
  let proteinDays = 0;
  let carbsDays = 0;
  let fatsDays = 0;
  let fibersDays = 0;
  let kCalDays = 0;

  nutritionPlan.forEach((plan) => {
    const nutritionStart = moment.utc(plan.startDate, "YYYYMMDD");
    const nutritionEnd = moment.utc(plan.endDate, "YYYYMMDD");
    const periodStart = moment.utc(startOfWeek, "YYYYMMDD");
    const periodEnd = moment.utc(endOfWeek, "YYYYMMDD").subtract(1, "day");

    const startDate = moment.max(nutritionStart, periodStart);
    const endDate = moment.min(nutritionEnd, periodEnd);

    // Number of days to get the total of the plan for the period of time
    const numberOfDays = endDate.diff(startDate, "days") + 1;
    // This is only for calculating the targets on the calendar part of the nutrition plans
    // As there will be separated rows for training and rest days
    // I know, it is a carbon copy of the other function but what can you do about it am I right.

    // If there is data, add that data to the variables and multiply for the number of days
    proteins += plan?.restDay?.proteins
      ? plan?.restDay?.proteins * numberOfDays
      : 0;
    fats += plan?.restDay?.fats ? plan?.restDay?.fats * numberOfDays : 0;
    carbs += plan?.restDay?.carbs ? plan?.restDay?.carbs * numberOfDays : 0;
    fibers += plan?.restDay?.fibers ? plan?.restDay?.fibers * numberOfDays : 0;

    // To accurately get the number of days for the average
    proteinDays += plan?.restDay?.proteins ? numberOfDays : 0;
    carbsDays += plan?.restDay?.carbs ? numberOfDays : 0;
    fatsDays += plan?.restDay?.fats ? numberOfDays : 0;
    fibersDays += plan?.restDay?.fibers ? numberOfDays : 0;

    const proteinsKcal = plan?.restDay?.proteins
      ? round(plan?.restDay?.proteins * 4, 0)
      : 0;
    const carbsKcal = plan?.restDay?.carbs
      ? round(plan?.restDay?.carbs * 4, 0)
      : 0;
    const fatsKcal = plan?.restDay?.fats
      ? round(plan?.restDay?.fats * 9, 0)
      : 0;
    const totalKCals = proteinsKcal + carbsKcal + fatsKcal;

    kCalDays += totalKCals ? numberOfDays : 0;

    kCals += totalKCals ? totalKCals * numberOfDays : 0;
  });

  // Condition in case the values are 0
  if (averageValues) {
    proteins = proteins / (proteinDays ? proteinDays : 1);
    fats = fats / (fatsDays ? fatsDays : 1);
    carbs = carbs / (carbsDays ? carbsDays : 1);
    fibers = fibers / (fibersDays ? fibersDays : 1);
    kCals = kCals / (kCalDays ? kCalDays : 1);
  }

  return {
    targetProteins: proteins,
    targetFats: fats,
    targetCalories: kCals,
    targetCarbs: carbs,
    targetFibers: fibers,
  };
};

export const getDateToDisplay = (utcDate) => {
  return moment(String(utcDate)).format("MMM D, YYYY");
};

export const getPeriodDateToDisplay = (dateStart, dateEnd) => {
  return `${moment(dateStart).format("MMM D")} - ${moment(dateEnd).format(
    "MMM D, YYYY"
  )}`;
};

// Get the nutrition plans for an specified period of time
export const getNutritionPlan = (
  nutritionPlans,
  utcDate,
  isPeriod,
  startOfWeek,
  endOfWeek
) => {
  let nutritionPlan;

  if (isPeriod) {
    // For getting the range of the week
    const startUtc = moment.utc(startOfWeek).format("YYYYMMDD");
    const endUtc = moment.utc(endOfWeek).subtract(1, "day").format("YYYYMMDD");

    // Finds the nutrition plans that exist in the period of time of the week
    nutritionPlan = nutritionPlans.filter((plan) =>
      isPlanInRange(plan, startUtc, endUtc)
    );
  } else {
    // Finds the plan that corresponds to the utcDate of the day
    nutritionPlan = nutritionPlans.find(
      (plan) => plan.utcStartDate <= utcDate && plan.utcEndDate >= utcDate
    );
  }

  return nutritionPlan;
};

// Gets the plans in a date range
// First it checks if the entire plan falls within the specified date range
// Second it checks if the start date of the plan falls within the specified date range.
// Finally it checks if the end date of the plan falls within the specified date range.
export const isPlanInRange = (plan, startUtc, endUtc) => {
  return (
    (plan.utcStartDate >= startUtc && plan.utcEndDate <= endUtc) ||
    (plan.utcStartDate <= startUtc && plan.utcEndDate >= startUtc) ||
    (plan.utcStartDate <= endUtc && plan.utcEndDate >= endUtc)
  );
};

export function getNutritionTargetsForPlanDay(
  nutritionPlans,
  dayKey,
  exercises
) {
  const dayUtc = parseInt(moment(dayKey, "YYYY-MM-DD").format("YYYYMMDD"), 10);
  const plan = nutritionPlans?.find(
    (p) => p.utcStartDate <= dayUtc && p.utcEndDate >= dayUtc
  );
  if (!plan) return null;

  const hasWorkout = (exercises?.length ?? 0) > 0;
  const { targetCalories, targetProteins, targetCarbs, targetFats } =
    getTargetData({ nutritionPlan: plan, isPeriod: false, hasWorkout });

  return {
    calories: targetCalories,
    proteins: targetProteins,
    carbs: targetCarbs,
    fats: targetFats,
  };
}

export const getWorkoutDaysOnRange = (
  startOfWeek,
  endOfWeek,
  activeExercises
) => {
  let workoutDays = 0;
  const startDate = moment(startOfWeek);
  while (startDate.isBefore(endOfWeek)) {
    const exercise = activeExercises.find(
      (ex) => ex.utcDate === Number(moment(startDate).format("YYYYMMDD"))
    );

    if (!!exercise) workoutDays += 1;

    startDate.add(1, "day");
  }
  return workoutDays;
};

/**
 * Parse the information adding a food to the display component
 * @param {*} item
 * @param {*} customQuantityNumberParam
 * @param {*} customUnitOfMeasure
 * @returns
 */
export const getItemsToAdd = (
  item,
  customQuantityNumberParam = 1,
  customUnitOfMeasure,
  includeMealReference = false
) => {
  const { type, foods, _id } = item;
  const customQuantityNumber = parseFloat(customQuantityNumberParam);
  return type === "meal"
    ? foods.map(({ foodId, quantityNumber, ...food }) => ({
        ...foodId,
        ...food,
        ...(includeMealReference ? { mealId: _id } : {}),
        customQuantityNumber: quantityNumber * customQuantityNumber,
        customUnitOfMeasure: food.unitOfMeasure || "custom",
      }))
    : [{ ...item, customQuantityNumber, customUnitOfMeasure }];
};

/**
 * Calculate the % of total calories that each macro (proteins, carbs, fats)
 * represents, using 4/4/9 kcal per gram.
 * @param {{ proteins: number, carbs: number, fats: number, calories: number }} macros
 * @returns {{ proteins: number, carbs: number, fats: number }} rounded percentages
 */
export const calculateMacroPercentages = (macros) => {
  const { proteins, carbs, fats, calories } = macros;
  if (!calories || calories === 0) {
    return { proteins: 0, carbs: 0, fats: 0 };
  }

  const proteinCalories = proteins * 4;
  const carbCalories = carbs * 4;
  const fatCalories = fats * 9;

  return {
    proteins: Math.round((proteinCalories / calories) * 100),
    carbs: Math.round((carbCalories / calories) * 100),
    fats: Math.round((fatCalories / calories) * 100),
  };
};
